import type { SurfacedItem } from '@app/core';
import { useTranslation } from 'react-i18next';

interface TimelineProps {
  items: SurfacedItem[];
  visible: boolean;
}

/**
 * Timeline — shows upcoming hard-deadline (warming with anchorDate) items.
 * Visible in daily engagement mode only per engagement config.
 */
export function Timeline({ items, visible }: TimelineProps) {
  const { t } = useTranslation();

  if (!visible) return null;

  // Filter to warming items with anchor dates, sorted by anchor date ascending
  const deadlineItems = items
    .filter(
      s =>
        s.item.curve.direction === 'warming' &&
        s.item.curve.anchorDate != null,
    )
    .sort((a, b) => {
      const aDate = a.item.curve.direction === 'warming' ? a.item.curve.anchorDate : undefined;
      const bDate = b.item.curve.direction === 'warming' ? b.item.curve.anchorDate : undefined;
      if (!aDate || !bDate) return 0;
      return aDate.getTime() - bDate.getTime();
    });

  if (deadlineItems.length === 0) return null;

  return (
    <div
      style={{
        maxWidth: '480px',
        width: '100%',
        margin: 'var(--space-lg) auto 0',
      }}
    >
      <h3
        style={{
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: 'var(--color-text-secondary)',
          marginBottom: 'var(--space-sm)',
        }}
      >
        {t('items:timeline.title')}
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
        {deadlineItems.map(s => {
          const anchor =
            s.item.curve.direction === 'warming' ? s.item.curve.anchorDate : undefined;
          return (
            <div
              key={s.item.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 'var(--space-xs) var(--space-sm)',
                background: 'var(--color-card)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-card-border)',
                fontSize: '0.8rem',
              }}
            >
              <span style={{ color: 'var(--color-text-primary)' }}>{s.item.text}</span>
              {anchor && (
                <span
                  style={{
                    color: 'var(--color-warm)',
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.7rem',
                    marginLeft: 'var(--space-sm)',
                    flexShrink: 0,
                  }}
                >
                  {anchor.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                  })}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
